import { DestroyRef, ErrorHandler, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  catchError,
  connectable,
  defer,
  filter,
  finalize,
  map,
  Observable,
  of,
  pairwise,
  ReplaySubject,
  retry,
  switchMap,
  take,
  throwError,
  timer,
} from 'rxjs';
import { AuthService } from '@grotem/grotem.box.angular.auth-lib';
import { DialogManagerService } from '@services/utils/dialog-manager.service';
import { DialogUnavailableError } from '@services/utils/dialog-unavailable.error';
import { ConsentStorageService } from '@services/utils/consent-storage.service';
import { PersonalDataAgreementService } from '@services/api/internal/personal-data-agreement.service';
import { ConsentDialogComponent } from '../../common/components/consent-dialog/consent-dialog.component';
import { UserFacade } from '../../store/user/user.facade';
import { PersonalDataAgreement } from '@shared/interfaces';
import { Maybe } from '@shared/types';

export const CONSENT_DIALOG_DELAY = 200;

@Injectable({
  providedIn: 'root',
})
export class ConsentDialogService {
  #dialogManagerService = inject(DialogManagerService);
  #consentStorageService = inject(ConsentStorageService);
  #personalDataAgreementService = inject(PersonalDataAgreementService);
  #userFacade = inject(UserFacade);
  #authService = inject(AuthService);
  #destroyRef = inject(DestroyRef);
  #errorHandler = inject(ErrorHandler);
  // The flow in progress: a re-created layout waits for it instead of starting a second one next to an open consent.
  #consentFlow$: Observable<void> | null = null;

  subscribeToLogout(): void {
    this.#authService.isAuthenticated$
      .pipe(
        pairwise(),
        filter(([prev, curr]) => prev === true && curr === false),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        const userId = this.#userFacade.$user()?.userId;
        if (userId) {
          this.#consentStorageService.clearDeclineTimestamp(userId);
        }
      });
  }

  /**
   * Starts the consent flow and returns a stream that emits once and completes when the flow is over (including every
   * skip branch and a failed agreements request; a failing consent dialog errors the stream instead). The flow runs
   * regardless of subscribers, so the consent result is saved even if the caller unsubscribes. While a flow is in
   * progress, repeated calls return it instead of starting another one.
   */
  openConsentDialog(isTurkishBuild: boolean): Observable<void> {
    if (this.#consentFlow$) {
      return this.#consentFlow$;
    }

    const consentFlow$ = connectable(
      this.runConsentFlow(isTurkishBuild).pipe(
        finalize(() => (this.#consentFlow$ = null)),
        takeUntilDestroyed(this.#destroyRef),
      ),
      { connector: () => new ReplaySubject<void>(1), resetOnDisconnect: false },
    );
    // Assigned before `connect`: skip branches complete synchronously inside it, and `finalize` must reset the field.
    this.#consentFlow$ = consentFlow$;
    consentFlow$.connect();

    return consentFlow$;
  }

  private runConsentFlow(isTurkishBuild: boolean): Observable<void> {
    if (!isTurkishBuild) {
      return of(undefined);
    }

    const userId = this.#userFacade.$user()?.userId;
    if (!userId) {
      return of(undefined);
    }

    if (this.#consentStorageService.isAllAccepted(userId)) {
      return of(undefined);
    }

    if (!this.#consentStorageService.canShowConsent(userId)) {
      return of(undefined);
    }

    return this.loadAgreementsAndShowSequence(userId);
  }

  private loadAgreementsAndShowSequence(userId: string): Observable<void> {
    const storedKeys = this.#consentStorageService.getAcceptedKeys(userId);

    return this.#personalDataAgreementService.getPersonalDataAgreement().pipe(
      // A failed request only skips consent this time; the error still reaches the global handler. Errors of the
      // dialog sequence itself are not caught, so the flow never reports completion while a consent may be open.
      catchError((error: unknown) => {
        this.#errorHandler.handleError(error);
        return of([]);
      }),
      // The backend answers `null` when there is no agreement text for the user's language: nothing to accept.
      map((agreements: Maybe<PersonalDataAgreement[]>) => (agreements ?? []).filter(agreement => !storedKeys.includes(agreement.key))),
      switchMap(missingAgreements =>
        missingAgreements.length === 0 ? of(undefined) : this.showConsentSequence(missingAgreements, userId, 0),
      ),
    );
  }

  private showConsentSequence(agreements: PersonalDataAgreement[], userId: string, index: number, hadDecline = false): Observable<void> {
    if (index >= agreements.length) {
      if (hadDecline) {
        this.#consentStorageService.saveDeclineTimestamp(userId);
      } else {
        this.#consentStorageService.markAllAccepted(userId);
      }
      return of(undefined);
    }

    const agreement = agreements[index];
    return defer(() =>
      of(
        this.#dialogManagerService.openCustomDialog(ConsentDialogComponent, {
          showHeader: false,
          width: '500px',
          closeOnEscape: false,
          dismissableMask: false,
          data: { agreement },
        }),
      ),
    ).pipe(
      retry({
        delay: (error: unknown) => (error instanceof DialogUnavailableError ? timer(CONSENT_DIALOG_DELAY) : throwError(() => error)),
      }),
      switchMap(dialogRef => {
        return dialogRef.onClose.pipe(
          take(1),
          switchMap(accepted =>
            timer(CONSENT_DIALOG_DELAY).pipe(
              switchMap(() => this.showConsentSequence(agreements, userId, index + 1, hadDecline || !accepted)),
            ),
          ),
        );
      }),
    );
  }
}
